/**
 * CollaboratorStack - overlapping avatars with a "+N" tail.
 *
 * Promoted verbatim from `Matters/surface/MatterCollaboratorStack`, which was
 * the only place in the app that got this right, and generalised over
 * `AccessPerson` so every page's header (and every context pane's People
 * section) draws the same faces.
 *
 * ONE BEHAVIOURAL CHANGE, AND IT IS THE POINT OF THE DEDUP: pressing the
 * stack OPENS THE ACCESS PANE, not the manage side panel. The avatars are the
 * affordance for "who can see this" - a READ - so they should answer that
 * question in place rather than throw a transactional editor over the page.
 * The pane's own footer button is the one way to the editor.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import ProfileImage from '../../Header/ProfileImage';
import { normaliseAll, ownerFirst } from './accessModel';

export interface CollaboratorStackProps {
  /** Raw rows in any backend shape - normalised here. */
  collaborators: any[];
  /** How many faces before the "+N". */
  max?: number;
  /**
   * The real number of people, when `collaborators` is only a preview of them.
   *
   * The dashboard endpoints send the first few collaborators plus a
   * `collaborator_count`, so "+N" has to count from the total rather than from
   * the array - otherwise a card holding 3 of 12 people claims there are 3.
   * Ignored when it is smaller than what was passed in.
   */
  totalCount?: number;
  /** Sized for a header (default), a context-pane row, or a card. */
  size?: 'md' | 'sm' | 'xs';
  /**
   * Append "(view)" / "(edit)" / "(admin)" to each name in the hover title.
   *
   * True for access surfaces, where the level is the point. FALSE for plain
   * membership stacks (a team's or department's Members column): those rows
   * carry no access field, so `normaliseAccessLevel` defaults them to `view`
   * and the title would assert a permission the backend never stated.
   */
  withAccessLabels?: boolean;
  /** Almost always "raise the Access pane". Omit for a decorative stack. */
  onClick?: () => void;
  /** Carried from whatever this replaced, so tour steps keep their anchor. */
  tourId?: string;
  testId?: string;
  className?: string;
}

const CollaboratorStack: React.FC<CollaboratorStackProps> = ({
  collaborators,
  max = 3,
  totalCount,
  size = 'sm',
  withAccessLabels = true,
  onClick,
  tourId,
  testId = 'collaborator-stack',
  className,
}) => {
  const { t } = useTranslation();
  const people = useMemo(() => ownerFirst(normaliseAll(collaborators)), [collaborators]);

  if (!people.length) return null;

  const visible = people.slice(0, max);
  const overflow = Math.max(totalCount ?? 0, people.length) - visible.length;
  const box = size === 'md' ? 'h-8 w-8' : size === 'xs' ? 'h-5 w-5' : 'h-6 w-6';
  const title = people
    .map((p) => (withAccessLabels ? `${p.name} (${p.access})` : p.name))
    .join(', ');

  // A `<button disabled>` swallows the click instead of letting it bubble, so a
  // decorative stack sitting on a clickable table row or card would punch a
  // dead hole in it. Render the element only when it is actually pressable.
  const Tag = onClick ? 'button' : 'span';

  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      data-tour-id={tourId}
      data-testid={testId}
      aria-label={t('Access')}
      title={title}
      className={clsx('flex items-center -space-x-2', onClick && 'cursor-pointer', className)}
    >
      {visible.map((person, index) => (
        <span key={person.id || person.email || index} className="rounded-full ring-2 ring-bg">
          <ProfileImage
            profilePicture={person.avatar}
            email={person.email || null}
            initials={person.initials}
            name={person.name}
            size={box}
          />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className={clsx(
            'flex items-center justify-center rounded-full ring-2 ring-bg',
            'bg-surface-sunken text-[10px] font-medium text-fg-body',
            box
          )}
        >
          +{overflow}
        </span>
      )}
    </Tag>
  );
};

export default CollaboratorStack;
